 
import { Component, type ErrorInfo, type ReactNode } from 'react';
import styled from '@emotion/styled';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${(props) => props.theme.spacing(4)};
  background: ${(props) => props.theme.colors.tertiary[1000]};
  color: ${(props) => props.theme.colors.neutral.white};
`;

const ErrorTitle = styled.h1`
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: ${(props) => props.theme.typography.h2.fontSize};
  font-weight: ${(props) => props.theme.typography.h2.fontWeight};
  color: ${(props) => props.theme.colors.state.alert[1000]};
  margin-bottom: ${(props) => props.theme.spacing(2)};
`;

const ErrorMessage = styled.p`
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: ${(props) => props.theme.typography.body3Regular.fontSize};
  color: ${(props) => props.theme.colors.text.mediumEmphasis};
  margin-bottom: ${(props) => props.theme.spacing(3)};
  text-align: center;
  max-width: 600px;
`;

const ErrorDetails = styled.details`
  margin-top: ${(props) => props.theme.spacing(2)};
  padding: ${(props) => props.theme.spacing(2)};
  background: ${(props) => props.theme.colors.tertiary[900]};
  border-radius: ${(props) => props.theme.shape.borderRadiusLarge};
  max-width: 800px;
  width: 100%;

  summary {
    cursor: pointer;
    font-family: ${(props) => props.theme.typography.fontFamily};
    font-size: ${(props) => props.theme.typography.body4Bold.fontSize};
    font-weight: ${(props) => props.theme.typography.body4Bold.fontWeight};
    color: ${(props) => props.theme.colors.text.mediumLowEmphasis};
    margin-bottom: ${(props) => props.theme.spacing(1)};
  }

  pre {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: ${(props) => props.theme.colors.state.alert[500]};
    overflow-x: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
`;

const ReloadButton = styled.button`
  padding: ${(props) => props.theme.spacing(1.5)} ${(props) => props.theme.spacing(4)};
  background: ${(props) => props.theme.colors.primary[1000]};
  color: ${(props) => props.theme.colors.neutral.white};
  border: none;
  border-radius: ${(props) => props.theme.shape.borderRadiusLarge};
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: ${(props) => props.theme.typography.body3Bold.fontSize};
  font-weight: ${(props) => props.theme.typography.body3Bold.fontWeight};
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error capturado por ErrorBoundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReload = (): void => {
    globalThis.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorContainer>
          <ErrorTitle>¡Oops! Algo salió mal</ErrorTitle>
          <ErrorMessage>
            Lo sentimos, ocurrió un error inesperado. Por favor, intenta recargar la página.
            Si el problema persiste, contacta al soporte técnico.
          </ErrorMessage>

          <ReloadButton onClick={this.handleReload}>
            Recargar página
          </ReloadButton>

          {import.meta.env.DEV && this.state.error && (
            <ErrorDetails>
              <summary>Detalles del error (solo en desarrollo)</summary>
              <pre>
                {this.state.error.toString()}
                {'\n\n'}
                {this.state.errorInfo?.componentStack}
              </pre>
            </ErrorDetails>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}
